"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import IconPillsView from "@/components/optilase/hub/icon-pills-view";
import WhyChooseView from "@/components/optilase/home/why-choose-view";
import { mergeSourced, type SourcedBlok } from "@/lib/sourced-blok";
import { regionFromPathname } from "@/lib/region";
import { surfaceFrom } from "@/components/optilase/surface";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { usePublicPathname } from "@/lib/public-pathname";

type Asset = { filename?: string; alt?: string; focus?: string };

type IconCard = SbBlokData & {
  icon?: Asset;
  title?: string;
  description?: string;
};

type CardsWithIconsFields = {
  title?: string;
  description?: string;
  list_heading?: string;
  layout?: string;
  heading_color?: string;
  image?: Asset;
  cards?: IconCard[];
  link_label?: string;
  link_url?: StoryblokLink;
  hide_link?: boolean;
  bg_color?: string;
};

type CardsWithIconsBlok = SbBlokData & SourcedBlok<CardsWithIconsFields>;

const FIELDS: (keyof CardsWithIconsFields)[] = [
  "title",
  "description",
  "list_heading",
  "layout",
  "heading_color",
  "image",
  "cards",
  "link_label",
  "link_url",
  "bg_color",
];

export default function GlobalCardsWithIcons({ blok: rawBlok }: { blok: CardsWithIconsBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const blok = mergeSourced(rawBlok, FIELDS);
  const link = resolveLink(blok.link_url, region);
  const cards = blok.cards ?? [];

  if (blok.layout === "pills") {
    return (
      <div {...storyblokEditable(rawBlok)}>
        <IconPillsView
          surface={surfaceFrom(blok.bg_color)}
          heading={blok.title ?? ""}
          intro={blok.description}
          listHeading={blok.list_heading}
          image={blok.image?.filename ?? ""}
          imageAlt={blok.image?.alt ?? ""}
          imageFocus={blok.image?.focus}
          headingTone={blok.heading_color === "teal" ? "teal" : "white"}
          pills={cards.map((c) => ({
            icon: c.icon?.filename ?? "",
            iconAlt: c.icon?.alt ?? "",
            label: c.title ?? "",
            body: c.description,
          }))}
        />
      </div>
    );
  }

  return (
    <div {...storyblokEditable(rawBlok)}>
      <WhyChooseView
        surface={surfaceFrom(blok.bg_color)}
        heading={blok.title ?? ""}
        cards={cards.map((c) => ({
          icon: c.icon?.filename ?? "",
          iconAlt: c.icon?.alt ?? "",
          title: c.title ?? "",
          body: c.description ?? "",
        }))}
        linkLabel={rawBlok.hide_link ? undefined : blok.link_label}
        linkHref={link.href}
      />
    </div>
  );
}
